import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { v4 as randomUUID } from 'uuid';
import dayjs, { Dayjs } from 'dayjs';

import { DayProperties } from '../../pages/Calendar/Helpers/interfaces';
import { UNSAVED_TARGET_DATA_WARNING } from './constants';
import { PERMISSIONS } from '../../App/constants';
import packageJson from '../../../package.json';

import { DispatchProperties, useSelector } from '../../redux/store';
import { refresh } from '../../redux/State/identitySlice/accountSlice';
import { getBranchId, getRefreshToken, getToken } from '../../redux/API/Main/helpers';
import { setDays } from '../../redux/State/clientSlice/calendarHelperSlice';
import { TargetSessionStatuses } from '../../redux/API/ClientAPIHelpers/sessionsProperties';
import { setBranchId } from '../../redux/API/Main/fetchAPI';

export const useRefreshToken = () => {
	const dispatch = useDispatch<DispatchProperties>();

	useEffect(() => {
		const storedToken = getToken();
		if (!storedToken) return;

		const parsedtoken = JSON.parse(storedToken) as { expires_in: number };
		const time = (parsedtoken.expires_in - 10) * 1000;

		const interval = setInterval(() => {
			const token = getRefreshToken();

			if (token) {
				dispatch(refresh(token));
			}
		}, time);

		/* eslint-disable  */
		return () => clearInterval(interval);
	}, [dispatch]);
};

export const useMultipleTabs = () => {
	useEffect(() => {
		const ls = window.localStorage;
		const ss = window.sessionStorage;
		const remember = JSON.parse(ls.getItem('remember') as string);

		if (!remember) {
			setTimeout(() => {
				const tabsArray: string[] = JSON.parse(ls.getItem('tabs') as string);
				const tab = ss.getItem('id');

				if (!tabsArray && tab) {
					const tabs = [tab];
					ls.setItem('tabs', JSON.stringify(tabs));
				}

				if (tabsArray && tab && !tabsArray.some((item) => item === tab)) {
					tabsArray.push(tab);
					ls.setItem('tabs', JSON.stringify(tabsArray));
				}
			}, 1000);
		}
	}, []);

	window.onbeforeunload = () => {
		const ls = window.localStorage;
		const ss = window.sessionStorage;

		const remember = JSON.parse(ls.getItem('remember') as string);

		if (!remember) {
			const tabsArray = JSON.parse(ls.getItem('tabs') as string) as string[];

			if (tabsArray && tabsArray.length > 1) {
				const tab = ss.getItem('id');
				const tabs = tabsArray.filter((item) => item !== tab);
				ls.setItem('tabs', JSON.stringify(tabs));
			}

			if (tabsArray && tabsArray.length === 1) {
				ls.clear();
			}
		}
	};
};

export const useScreenWidth = () => {
	const [screenWidth, setScreenWidth] = useState<number>(window.innerWidth);

	useEffect(() => {
		window.addEventListener('resize', () => setScreenWidth(window.innerWidth));

		return () => window.removeEventListener('resize', () => setScreenWidth(window.innerWidth));
	}, []);

	return screenWidth;
};

export const useInitializeDays = () => {
	const dispatch = useDispatch<DispatchProperties>();

	const weekDay = useSelector((state) => state.calendarHelper.weekDay);
	const month = useSelector((state) => state.calendarHelper.month);
	const year = useSelector((state) => state.calendarHelper.year);
	const lastDayOfPreviousMonth = useSelector((state) => state.calendarHelper.lastDayOfPreviousMonth);
	const lastDayOfMonth = useSelector((state) => state.calendarHelper.lastDayOfMonth);

	useEffect(() => {
		const previousMonthDays: DayProperties[] = [];

		for (let i = weekDay; i > 0; i -= 1) {
			previousMonthDays.push({
				day: lastDayOfPreviousMonth - i + 1,
				status: 0,
				month: month - 1 < 0 ? 11 : month - 1,
				year: month - 1 < 0 ? year - 1 : year,
			});
		}

		const currentMonthDays: DayProperties[] = [];

		for (let i = 1; i <= lastDayOfMonth; i += 1) {
			currentMonthDays.push({
				day: i,
				status: 1,
				month,
				year,
			});
		}

		const nextMonthDays: DayProperties[] = [];
		const lastWeekDay = new Date(year, month + 1, 0).getDay();

		for (let i = 1; i < 7 - lastWeekDay; i += 1) {
			nextMonthDays.push({
				day: i,
				status: 2,
				month: month + 1 < 12 ? month + 1 : 0,
				year: month + 1 < 12 ? year : year + 1,
			});
		}
		dispatch(setDays([...previousMonthDays, ...currentMonthDays, ...nextMonthDays]));
	}, [weekDay, lastDayOfMonth, year, month, lastDayOfPreviousMonth, dispatch]);
};

export const useInitializeTabId = () => {
	useEffect(() => {
		const remember = JSON.parse(window.localStorage.getItem('remember') as string);
		const tab = window.sessionStorage.getItem('id');

		if (!tab && !remember) {
			window.sessionStorage.setItem('id', randomUUID());
		}
	}, []);
};

export const useCompareDates = (startDate: Dayjs | null, endDate: Dayjs | null) => {
	const [errorMessage, setErrorMessage] = useState<string>('');

	useEffect(() => {
		if (!startDate || !endDate) return;

		if (dayjs(startDate).isBefore(endDate) || dayjs(startDate).isSame(endDate)) {
			setErrorMessage('');

			return;
		}
		setErrorMessage('End date should be greater than start date');
	}, [startDate, endDate]);

	if (!startDate || !endDate)
		return {
			message: '',
			hasError: false,
		};

	return {
		message: errorMessage,
		hasError: Boolean(errorMessage),
	};
};

export const useCheckPermission = (permission: string | null) => {
	const role = useSelector((state) => state.account.role);
	if (!role) return { permissionGranted: false };
	if (!permission) return { permissionGranted: true };
	const claims: string[] = role.claims.map((x) => x.claimValue);
	const permissionGranted =
		claims.includes(PERMISSIONS.SUPERADMIN) ||
		claims.includes(permission) ||
		claims.some((x) => x.includes(permission));

	return { permissionGranted };
};

export const useCheckVersion = () => {
	useEffect(() => {
		const savedVersion = localStorage.getItem('appVersion');

		if (savedVersion !== packageJson.version) {
			if (window.confirm('A new version is available. Refresh now?')) {
				localStorage.setItem('appVersion', packageJson.version);
				window.location.reload();
			}
		}
	}, []);
};

// Flag to bypass browser warnings when user already confirmed via custom modal for log out function
let navigationConfirmed = false;

export const useBeforeUnloadWarning = (hasUnsavedTargets: boolean) => {
	useEffect(() => {
		const handleBeforeUnload = (event: BeforeUnloadEvent) => {
			if (hasUnsavedTargets && !navigationConfirmed) {
				event.preventDefault();
			}
		};

		window.addEventListener('beforeunload', handleBeforeUnload);

		return () => {
			window.removeEventListener('beforeunload', handleBeforeUnload);
		};
	}, [hasUnsavedTargets]);

	useEffect(() => {
		if (!hasUnsavedTargets) return;

		const handlePopState = () => {
			if (navigationConfirmed) {
				navigationConfirmed = false;

				return;
			}
			const confirmLeave = window.confirm(UNSAVED_TARGET_DATA_WARNING);

			if (!confirmLeave) {
				window.history.pushState(null, '', window.location.href);
			}
		};

		window.history.pushState(null, '', window.location.href);
		window.addEventListener('popstate', handlePopState);

		return () => {
			window.removeEventListener('popstate', handlePopState);
		};
	}, [hasUnsavedTargets]);
};

export const useNavigationWarning = () => {
	const { pathname } = useLocation();
	const targets = useSelector((state) => state.session.targets);

	const [showWarning, setShowWarning] = useState(false);
	const [pendingNavigation, setPendingNavigation] = useState<(() => void) | null>(null);

	const isOnDataCollectionPage = pathname.includes('/calendar/session/');
	const hasUnsavedTargets =
		isOnDataCollectionPage && targets.some((target) => target.targetStatus === TargetSessionStatuses.open);

	const checkAndNavigate = useCallback(
		(navigationFn: () => void) => {
			if (hasUnsavedTargets) {
				setPendingNavigation(() => navigationFn);
				setShowWarning(true);
			} else {
				navigationFn();
			}
		},
		[hasUnsavedTargets],
	);

	const handleWarningConfirm = useCallback(() => {
		setShowWarning(false);
		navigationConfirmed = true;

		if (pendingNavigation) {
			pendingNavigation();
			setPendingNavigation(null);
		}
	}, [pendingNavigation]);

	const handleWarningCancel = useCallback(() => {
		setShowWarning(false);
		setPendingNavigation(null);
	}, []);

	return {
		hasUnsavedTargets,
		showWarning,
		checkAndNavigate,
		handleWarningConfirm,
		handleWarningCancel,
	};
};

export const useShowBTsSessions = (eventUserId: string, userId: string) => {
	const opacity = eventUserId && userId && eventUserId !== userId ? 0.4 : 1;

	return opacity;
};

export const useSetBranch = () => {
	useEffect(() => {
		const branchId = getBranchId();
		if (!branchId) return;
		setBranchId(branchId);
	}, []);
};
